import { NavLink } from 'react-router-dom'
import {
  LayoutDashboard, Play, ChevronLeft, ChevronRight,
  ShieldCheck, Settings, HelpCircle,
  Repeat,
} from 'lucide-react'
import { useUIStore } from '../../store/uiStore'
import { useAuthStore } from '../../store/authStore'
import { cn } from '../../utils'
import { env } from '../../config/env'
// import BridgerLogo from '../../assets/bridger-primary-logo.svg'
import BridgerTextLogo from '../../assets/bridger-primary-logo.svg'
import BridgerIconLogo from '../../assets/bridger-primary-logo-cropped.svg'

/**
 * NAV_CONFIG — role-based sidebar structure.
 * Add new tools here as new modules are built.
 * Each group can have any number of items.
 */
const NAV_CONFIG = {
  ADMIN: [
    {
      group: 'Audit Policy',
      icon: ShieldCheck,
      items: [
        { label: 'Run Audit', to: '/audit/run', icon: Play },
      ],
    },
    {
      group: 'Regression Suite',
      icon: ShieldCheck,
      items: [
        { label: 'Run Regression', to: '/regression/run', icon: Repeat },
      ],
    },
  ],

  POLICY_AUDIT_USER: [
    {
      group: 'Audit Policy',
      icon: ShieldCheck,
      items: [
        { label: 'Run Audit', to: '/audit/run', icon: Play },
      ],
    },
  ],

  REGRESSION_RUNNER: [
    {
      group: 'Regression Suite',
      icon: ShieldCheck,
      items: [
        { label: 'Run Regression', to: '/regression/run', icon: Repeat },
      ],
    },
  ],
}

    // Future tools:
    // {
    //   group: 'Claims Audit',
    //   icon: FileSearch,
    //   items: [
    //     { label: 'Dashboard', to: '/claims/dashboard', icon: LayoutDashboard },
    //     { label: 'Run Audit', to: '/claims/run', icon: Play },
    //   ],
    // },
  

  // VIEWER: [
  //   {
  //     group: 'Audit Policy',
  //     icon: ShieldCheck,
  //     items: [
  //       // { label: 'Dashboard', to: '/audit/dashboard', icon: LayoutDashboard },
  //     ],
  //   },
  // ],


function NavItem({ item, collapsed }) {
  const Icon = item.icon

  return (
    <NavLink
      to={item.to}
      className={({ isActive }) =>
        cn(
          'group flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all',
          'focus:outline-none focus:ring-2 focus:ring-blue-500/50',
          isActive
            ? 'bg-blue-700 text-white shadow-sm'
            : 'text-slate-300 hover:bg-slate-700 hover:text-white'
        )
      }
      title={collapsed ? item.label : undefined}
    >
      <Icon className="h-4 w-4 shrink-0" />
      {!collapsed && <span className="truncate">{item.label}</span>}
    </NavLink>
  )
}

export default function Sidebar() {
  const collapsed    = useUIStore((s) => s.sidebarCollapsed)
  const toggleSidebar = useUIStore((s) => s.toggleSidebar)
  const user         = useAuthStore((s) => s.user)
  const navGroups    = NAV_CONFIG[user?.role] || []

  return (
    <aside
      className={cn(
        'flex flex-col bg-slate-900 border-r border-slate-800 transition-all duration-200 ease-in-out',
        'min-h-screen shrink-0',
        collapsed ? 'w-16' : 'w-64'
      )}
    >
{/* Logo row */}
<div
  className={cn(
    'flex items-center border-b border-slate-800 h-[64px] px-3 shrink-0',
    collapsed ? 'justify-center' : 'justify-between'
  )}
>
  {!collapsed && (
    <>
      {/* Icon + Text grouped together */}
      <div className="flex items-center gap-2 min-w-0">
        <img
          src={BridgerIconLogo}
          alt="Bridger Icon"
          className="h-8 w-8 object-contain shrink-0"
        />
        <div className="flex flex-col min-w-0">
          <img
            src={BridgerTextLogo}
            alt="Bridger Insurance"
            className="h-5 object-contain object-left"
          />
          <span className="text-[#00b4d8] text-[10px] font-semibold tracking-widest uppercase leading-none mt-0.5">
            SmartOps
          </span>
        </div>
      </div>

      {/* Collapse button */}
      <button
        onClick={toggleSidebar}
        className="text-slate-400 hover:text-white hover:bg-slate-700 p-1.5 rounded-md transition-colors shrink-0"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
    </>
  )}

  {/* Collapsed — icon only */}
  {collapsed && (
    <img
      src={BridgerIconLogo}
      alt="Bridger"
      className="h-8 w-8 object-contain"
    />
  )}
</div>

      {/* Nav groups */}
      <nav className="flex-1 overflow-y-auto scrollbar-thin px-2 py-4 space-y-6">
        {navGroups.map((group) => (
          <div key={group.group}>
            {!collapsed && (
              <p className="px-3 mb-2 text-14px] font-bold uppercase tracking-widest text-white">
                {group.group}
              </p>
            )}
            {collapsed && <div className="mb-1 border-t border-slate-800 mx-2" />}
            <div className="space-y-0.5">
              {group.items.map((item) => (
                <NavItem key={item.to} item={item} collapsed={collapsed} />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Bottom section */}
      <div className="shrink-0 border-t border-slate-800 px-2 py-3 space-y-0.5">
        {!collapsed && (
          <NavLink
            to="/settings"
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-slate-400 hover:bg-slate-700 hover:text-white transition-colors"
          >
            <Settings className="h-4 w-4 shrink-0" />
            <span>Settings</span>
          </NavLink>
        )}
        {/* Expand toggle when collapsed */}
        {collapsed && (
          <button
            onClick={toggleSidebar}
            className="w-full flex justify-center p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
            aria-label="Expand sidebar"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        )}
        {!collapsed && (
          <p className="px-3 pt-2 text-[10px] text-slate-600">
            v{env.appVersion}
          </p>
        )}
      </div>
    </aside>
  )
}